import { User, Building2, Shield, MapPin } from "lucide-react";

const AboutSection = () => {
  return (
    <section id="about" className="py-24 lg:py-32 bg-white" data-testid="about-section">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          {/* Image Column */}
          <div className="lg:col-span-5 flex justify-center">
            <div className="relative w-[40%] min-w-[200px]">
              {/* Main Image Container - Reduced size by 60% */}
              <div className="relative rounded-lg overflow-hidden shadow-xl">
                <div className="aspect-[4/5] relative">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_decentralized-doors/artifacts/f45tsfj2_IMG_9056.jpg"
                    alt="Christopher Ferrer - Founder & CEO of MyDoor360"
                    className="w-full h-full object-cover"
                  />
                  {/* Overlay with name */}
                  <div className="absolute top-0 left-0 right-0 bg-gradient-to-b from-black/70 to-transparent p-3" data-testid="founder-image-overlay">
                    <h3 
                      className="text-sm font-bold text-white mb-0.5 leading-tight"
                      style={{ fontFamily: 'Outfit, sans-serif' }}
                    >
                      Christopher Ferrer
                    </h3>
                    <p className="text-[#C04000] font-medium text-xs">Founder & CEO</p>
                  </div>
                </div>
              </div>
              
              {/* Experience Badge */}
              <div className="absolute -bottom-4 -right-4 bg-white rounded-lg shadow-xl p-3 border border-slate-200">
                <div className="flex items-center gap-2">
                  <div className="w-8 h-8 bg-[#C04000]/10 rounded-lg flex items-center justify-center">
                    <Building2 className="w-4 h-4 text-[#C04000]" />
                  </div>
                  <div>
                    <p className="text-sm font-bold text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>NYC</p>
                    <p className="text-xs text-slate-500">Real Estate Expert</p>
                  </div>
                </div>
              </div>

              {/* Decorative Element */}
              <div className="absolute -top-3 -left-3 w-16 h-16 border-4 border-[#C04000]/20 rounded-lg -z-10"></div>
            </div>
          </div>

          {/* Content Column */}
          <div className="lg:col-span-7">
            <span className="text-xs font-semibold uppercase tracking-[0.2em] text-[#C04000] mb-4 block">
              Meet The Founder
            </span>
            <h2 
              className="text-3xl lg:text-4xl font-bold tracking-tight text-slate-900 mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="about-heading"
            >
              Christopher Ferrer
            </h2>
            
            {/* Location Tag */}
            <div className="flex items-center gap-2 mb-6 text-slate-600">
              <MapPin className="w-4 h-4 text-[#C04000]" />
              <span className="text-sm font-medium">Brooklyn, New York</span>
            </div>

            <div className="space-y-4 text-slate-600 leading-relaxed">
              <p>
                Christopher Ferrer is a <span className="font-semibold text-slate-800">Former Licensed Real Estate Agent</span> from Brooklyn, New York, 
                who worked extensively in <span className="font-semibold text-slate-800">Brooklyn Heights</span>, 
                <span className="font-semibold text-slate-800"> Brownstone Brooklyn</span>, and 
                <span className="font-semibold text-slate-800"> Manhattan</span>.
              </p>
              
              <p>
                Today, Christopher works in the property management side of the real estate business 
                but has never stopped innovating. He continuously develops ideas on how to streamline 
                real estate transactions and add extra security for homeowners and renters alike—leading 
                to the creation of <span className="font-semibold text-[#C04000]">MyDoor360</span>.
              </p>
              
              <p>
                In his spare time, Christopher enjoys studying cybersecurity, snowboarding, playing golf, 
                and spending quality time with his family and friends. He is constantly thinking of ways 
                to use technology to make people's daily lives more safe, secure, and affordable.
              </p>
            </div>

            {/* Expertise Tags */}
            <div className="mt-8 flex flex-wrap gap-3">
              {[
                "Real Estate",
                "Property Management", 
                "Cybersecurity",
                "Blockchain Technology",
                "Transaction Security"
              ].map((tag) => (
                <span 
                  key={tag}
                  className="px-4 py-2 bg-slate-100 text-slate-700 text-sm font-medium rounded-full"
                >
                  {tag}
                </span>
              ))}
            </div>

            {/* Mission Statement */}
            <div className="mt-8 p-6 bg-[#FFF3ED] rounded-lg border border-[#C04000]/20">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-[#C04000]/20 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Shield className="w-5 h-5 text-[#C04000]" />
                </div>
                <div>
                  <h4 
                    className="font-semibold text-slate-900 mb-2"
                    style={{ fontFamily: 'Outfit, sans-serif' }}
                  >
                    Our Mission
                  </h4>
                  <p className="text-slate-600 text-sm italic">
                    "Using technology to make people's daily lives more safe, secure, and affordable—starting 
                    with revolutionizing how we buy, sell, and rent real estate."
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default AboutSection;
